import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import {
  fetchFranchisees,
  fetchFranchiseeAssignments,
  assignRestaurantToFranchisee,
  revokeRestaurantFromFranchisee,
} from '../../../services/franchiseeService';
import { supabase } from '../../../lib/supabase';

/* ─── Assign Restaurants Modal ─────────────────────────────────────── */
const AssignRestaurantsModal = ({ franchisee, allRestaurants, currentAssignments, onClose, onUpdated, addToast, currentUserId }) => {
  const assignedIds = new Set(currentAssignments?.map((a) => a?.restaurant_id));
  const [pendingAdd, setPendingAdd] = useState(new Set());
  const [pendingRemove, setPendingRemove] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const toggle = (restaurantId) => {
    const isAssigned = assignedIds?.has(restaurantId);
    if (isAssigned) {
      setPendingRemove((prev) => {
        const next = new Set(prev);
        next?.has(restaurantId) ? next?.delete(restaurantId) : next?.add(restaurantId);
        return next;
      });
    } else {
      setPendingAdd((prev) => {
        const next = new Set(prev);
        next?.has(restaurantId) ? next?.delete(restaurantId) : next?.add(restaurantId);
        return next;
      });
    }
  };

  const isEffectivelyAssigned = (restaurantId) => {
    if (pendingRemove?.has(restaurantId)) return false;
    if (pendingAdd?.has(restaurantId)) return true;
    return assignedIds?.has(restaurantId);
  };

  const handleSave = async () => {
    if (!pendingAdd?.size && !pendingRemove?.size) {
      onClose();
      return;
    }
    setLoading(true);
    try {
      await Promise.all([
        ...[...pendingAdd]?.map((rId) =>
          assignRestaurantToFranchisee(franchisee?.id, rId, currentUserId)
        ),
        ...[...pendingRemove]?.map((rId) =>
          revokeRestaurantFromFranchisee(franchisee?.id, rId)
        ),
      ]);
      addToast(`Accès mis à jour pour ${franchisee?.full_name || franchisee?.email}`, 'success');
      onUpdated();
      onClose();
    } catch (err) {
      addToast(err?.message || 'Mise à jour échouée', 'error');
    } finally {
      setLoading(false);
    }
  };

  const changeCount = pendingAdd?.size + pendingRemove?.size;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Store" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Assigner des restaurants</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{franchisee?.full_name || franchisee?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {allRestaurants?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Aucun restaurant disponible.</p>
          ) : (
            <div className="space-y-2">
              {allRestaurants?.map((r) => {
                const effective = isEffectivelyAssigned(r?.id);
                const wasAssigned = assignedIds?.has(r?.id);
                const isAdding = pendingAdd?.has(r?.id);
                const isRemoving = pendingRemove?.has(r?.id);
                return (
                  <label key={r?.id} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 cursor-pointer transition-all group">
                    <input
                      type="checkbox"
                      checked={effective}
                      onChange={() => toggle(r?.id)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r?.display_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{r?.slug}</p>
                    </div>
                    {isAdding && (
                      <span className="text-xs font-medium text-green-600 bg-green-500/10 px-2 py-0.5 rounded-full flex-shrink-0">+ Ajout</span>
                    )}
                    {isRemoving && (
                      <span className="text-xs font-medium text-destructive bg-destructive/10 px-2 py-0.5 rounded-full flex-shrink-0">- Retrait</span>
                    )}
                    {!isAdding && !isRemoving && wasAssigned && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full flex-shrink-0">Assigné</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex gap-3">
          <Button onClick={handleSave} disabled={loading} className="flex-1 flex items-center justify-center gap-2">
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Enregistrement...</>
            ) : (
              <><Icon name="Save" size={15} />{changeCount > 0 ? `Enregistrer (${changeCount} changement${changeCount > 1 ? 's' : ''})` : 'Fermer'}</>
            )}
          </Button>
          <Button variant="outline" onClick={onClose} className="flex-shrink-0">Annuler</Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Create Franchisee Modal ──────────────────────────────────────── */
const CreateFranchiseeModal = ({ onClose, onCreated, addToast }) => {
  const [form, setForm] = useState({ email: '', full_name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.email?.trim()) {
      setError('Le courriel est requis.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Create user_profile directly with franchisee role
      // We upsert into user_profiles — the franchisee will set their password via invite/reset
      const { data: existingUser } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, role')
        ?.eq('email', form?.email?.trim())
        ?.maybeSingle();

      if (existingUser) {
        if (existingUser?.role === 'franchisee') {
          setError('Un franchisé avec ce courriel existe déjà.');
          return;
        }
        // Update existing user to franchisee role
        const { error: updateErr } = await supabase
          ?.from('user_profiles')
          ?.update({ role: 'franchisee', full_name: form?.full_name?.trim() || existingUser?.full_name, updated_at: new Date()?.toISOString() })
          ?.eq('id', existingUser?.id);
        if (updateErr) throw updateErr;
        addToast(`${form?.email} promu au rôle Franchisé.`, 'success');
        onCreated();
        onClose();
        return;
      }

      // Invite new user via edge function
      const { data, error: inviteErr } = await supabase?.functions?.invoke('invite-restaurant-member', {
        body: { email: form?.email?.trim(), role: 'franchisee', full_name: form?.full_name?.trim() || '' },
      });
      if (inviteErr || data?.error) throw new Error(data?.error || inviteErr?.message || 'Invitation échouée');
      addToast(`Invitation envoyée à ${form?.email}`, 'success');
      onCreated();
      onClose();
    } catch (err) {
      setError(err?.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="UserPlus" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Nouveau Franchisé</h2>
              <p className="text-xs text-muted-foreground">Inviter ou promouvoir un utilisateur</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Courriel <span className="text-destructive">*</span></label>
            <Input type="email" value={form?.email} onChange={(e) => setForm((p) => ({ ...p, email: e?.target?.value }))} placeholder="franchisee@example.com" autoFocus required />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Nom complet (optionnel)</label>
            <Input value={form?.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e?.target?.value }))} placeholder="Jean Dupont" />
          </div>
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Création...</>
              ) : (
                <><Icon name="UserPlus" size={15} />Créer Franchisé</>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">Annuler</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Franchisee Manager Panel ─────────────────────────────────────── */
const FranchiseeManager = ({ allRestaurants, addToast, currentUserId }) => {
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignTargetAssignments, setAssignTargetAssignments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');

  const loadFranchisees = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFranchisees();
      setFranchisees(data);
    } catch (err) {
      addToast('Erreur de chargement des franchisés: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadFranchisees();
  }, [loadFranchisees]);

  const handleOpenAssign = async (franchisee) => {
    try {
      const assignments = await fetchFranchiseeAssignments(franchisee?.id);
      setAssignTargetAssignments(assignments);
      setAssignTarget(franchisee);
    } catch (err) {
      addToast('Erreur: ' + (err?.message || ''));
    }
  };

  const handleRemoveFranchisee = async (franchisee) => {
    if (!window.confirm(`Révoquer le rôle franchisé de ${franchisee?.email} ?`)) return;
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ role: 'restaurant_admin', updated_at: new Date()?.toISOString() })
        ?.eq('id', franchisee?.id);
      if (error) throw error;
      addToast(`Rôle franchisé révoqué pour ${franchisee?.email}`, 'success');
      loadFranchisees();
    } catch (err) {
      addToast(err?.message || 'Erreur lors de la révocation', 'error');
    }
  };

  const filtered = franchisees?.filter((f) =>
    f?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
    f?.full_name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Franchisés</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {franchisees?.length} franchisé{franchisees?.length !== 1 ? 's' : ''} sur la plateforme
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder="Rechercher..."
              className="pl-8 pr-3 h-9 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors w-44 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)} className="flex items-center gap-2 h-9 text-sm flex-shrink-0">
            <Icon name="UserPlus" size={14} />
            Nouveau Franchisé
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center px-6">
          <div className="w-12 h-12 bg-muted rounded-2xl flex items-center justify-center mb-3">
            <Icon name="Building2" size={20} className="text-muted-foreground" />
          </div>
          {search ? (
            <p className="text-sm text-muted-foreground">Aucun résultat pour "{search}"</p>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground mb-1">Aucun franchisé</p>
              <p className="text-xs text-muted-foreground mb-4">Créez le premier franchisé pour lui assigner des restaurants.</p>
              <Button size="sm" onClick={() => setShowCreate(true)} className="flex items-center gap-2">
                <Icon name="UserPlus" size={14} />
                Nouveau Franchisé
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {filtered?.map((franchisee) => (
            <div key={franchisee?.id} className="flex items-center gap-4 px-6 py-4 hover:bg-muted/20 transition-colors group">
              <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={16} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{franchisee?.full_name || '—'}</p>
                <p className="text-xs text-muted-foreground truncate">{franchisee?.email}</p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary flex-shrink-0">
                <Icon name="Building2" size={11} />
                Franchisé
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs flex items-center gap-1.5 h-8"
                  onClick={() => handleOpenAssign(franchisee)}
                >
                  <Icon name="Store" size={12} />
                  <span className="hidden sm:inline">Restaurants</span>
                </Button>
                <button
                  onClick={() => handleRemoveFranchisee(franchisee)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                  title="Révoquer le rôle franchisé"
                >
                  <Icon name="UserX" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {assignTarget && (
        <AssignRestaurantsModal
          franchisee={assignTarget}
          allRestaurants={allRestaurants}
          currentAssignments={assignTargetAssignments}
          onClose={() => { setAssignTarget(null); setAssignTargetAssignments([]); }}
          onUpdated={loadFranchisees}
          addToast={addToast}
          currentUserId={currentUserId}
        />
      )}
      {showCreate && (
        <CreateFranchiseeModal
          onClose={() => setShowCreate(false)}
          onCreated={loadFranchisees}
          addToast={addToast}
        />
      )}
    </div>
  );
};

export default FranchiseeManager;
